"use client";

import {
  BarChart3,
  Boxes,
  CreditCard,
  ExternalLink,
  Home,
  LogOut,
  Menu,
  Moon,
  MoreHorizontal,
  PackageCheck,
  Settings,
  Star,
  Store,
  Sun,
  Wallet,
  X,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { apiGet } from "@/services/api";
import { cn } from "@/lib/utils";

const links = [
  { href: "/vendor", label: "Overview", icon: Home },
  { href: "/vendor/products", label: "Products", icon: Boxes },
  { href: "/vendor/orders", label: "Orders", icon: PackageCheck },
  { href: "/vendor/earnings", label: "Earnings", icon: BarChart3 },
  { href: "/vendor/withdrawals", label: "Withdrawals", icon: Wallet },
  { href: "/vendor/reviews", label: "Reviews", icon: Star },
  { href: "/vendor/settings", label: "Store settings", icon: Settings },
  { href: "/", label: "Storefront", icon: CreditCard },
];

export function VendorShell({ children, title: _title }: { children: React.ReactNode; title: string }) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session } = useSession();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [vendorStatus, setVendorStatus] = useState<string | null>(null);
  const [vendorBusinessName, setVendorBusinessName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    let isMounted = true;
    apiGet<{ vendor: { status: string; businessName?: string } }>("/api/vendors/me")
      .then((data) => {
        if (isMounted) {
          setVendorStatus(data?.vendor?.status ?? null);
          setVendorBusinessName(data?.vendor?.businessName ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Prevent background scrolling when mobile drawer is open
  useEffect(() => {
    if (mobileDrawerOpen) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setMobileDrawerOpen(false);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [mobileDrawerOpen]);

  // Close drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  const isAdmin = session?.user?.role === "ADMIN" || session?.user?.role === "ADMIN1";
  const isApproved = isAdmin || vendorStatus === "ACTIVE" || vendorStatus === "APPROVED";

  // When vendor is not approved, only show the Overview tab in the sidebar
  const visibleLinks = isApproved
    ? links
    : links.filter((link) => link.href === "/vendor");

  // Only restrict payouts & order processing while unapproved; allow product submissions and settings
  useEffect(() => {
    const restricted = ["/vendor/withdrawals", "/vendor/earnings", "/vendor/orders"];
    if (!loading && !isApproved && restricted.some((r) => pathname.startsWith(r))) {
      router.replace("/vendor");
    }
  }, [loading, isApproved, pathname, router]);

  const userInitials = (
    vendorBusinessName ||
    session?.user?.name ||
    session?.user?.email ||
    "V"
  )
    .slice(0, 2)
    .toUpperCase();

  // Mobile Bottom Navigation Tabs (Primary 4 items + Menu trigger)
  const mobileNavItems = isApproved
    ? [
        { href: "/vendor", label: "Overview", icon: Home, active: pathname === "/vendor" },
        { href: "/vendor/products", label: "Products", icon: Boxes, active: pathname.startsWith("/vendor/products") },
        { href: "/vendor/orders", label: "Orders", icon: PackageCheck, active: pathname.startsWith("/vendor/orders") },
        { href: "/vendor/earnings", label: "Earnings", icon: BarChart3, active: pathname.startsWith("/vendor/earnings") },
      ]
    : [
        { href: "/vendor", label: "Overview", icon: Home, active: pathname === "/vendor" },
        { href: "/vendor/products", label: "Products", icon: Boxes, active: pathname.startsWith("/vendor/products") },
        { href: "/vendor/settings", label: "Settings", icon: Settings, active: pathname.startsWith("/vendor/settings") },
        { href: "/", label: "Store", icon: Store, active: false },
      ];

  return (
    <div className="min-h-screen bg-amazon-page dark:bg-slate-950 flex flex-col text-slate-900 dark:text-slate-100">
      {/* ============================================================== */}
      {/* MOBILE STICKY TOP APP BAR (Visible on mobile & tablet: < lg)   */}
      {/* ============================================================== */}
      <header className="sticky top-0 z-30 flex h-14 w-full items-center justify-between border-b border-slate-200/90 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-3 sm:px-4 lg:hidden shadow-2xs">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10 active:scale-95 transition cursor-pointer"
            aria-label="Open vendor navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link href="/vendor" className="flex items-center gap-1.5 select-none">
            <span className="text-lg font-black tracking-wider text-slate-900 dark:text-white">MOVA</span>
            <span className="rounded bg-amazon-orange px-1.5 py-0.5 text-[10px] font-black uppercase text-slate-950 shadow-xs">
              Seller
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {!isApproved && !loading && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Review
            </span>
          )}

          <Link
            href="/"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10 transition"
            title="Open customer storefront"
          >
            <Store className="h-4 w-4 text-amazon-gold" />
          </Link>

          <button
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10 transition cursor-pointer"
            aria-label="Toggle theme"
          >
            {mounted && theme === "dark" ? <Sun className="h-4 w-4 text-amazon-gold" /> : <Moon className="h-4 w-4" />}
          </button>

          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-amazon-orange text-xs font-black text-slate-950 shadow-xs active:scale-95 transition ml-0.5 cursor-pointer"
            aria-label="Open seller account options"
          >
            {userInitials}
          </button>
        </div>
      </header>

      {/* ============================================================== */}
      {/* MOBILE SLIDE-OUT DRAWER (Off-canvas sidebar sheet for < lg)    */}
      {/* ============================================================== */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
            onClick={() => setMobileDrawerOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer sheet */}
          <div className="fixed inset-y-0 left-0 flex w-full max-w-[300px] flex-col justify-between bg-white dark:bg-slate-900 shadow-2xl p-4 sm:p-5 animate-in slide-in-from-left duration-200 border-r border-slate-200 dark:border-white/10">
            <div>
              {/* Drawer Header */}
              <div className="flex items-center justify-between pb-3.5 mb-3 border-b border-slate-200 dark:border-white/10">
                <Link
                  href="/vendor"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="flex items-center gap-2"
                >
                  <span className="text-xl font-black tracking-wider text-slate-900 dark:text-white">MOVA</span>
                  <span className="rounded bg-amazon-orange px-2 py-0.5 text-[10px] font-black uppercase text-slate-950 shadow-xs">
                    Seller
                  </span>
                </Link>

                <button
                  type="button"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white transition cursor-pointer"
                  aria-label="Close navigation drawer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Vendor Account Banner in Drawer */}
              <div className="mb-4 rounded-xl border border-slate-200/80 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-slate-800/60">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amazon-orange text-sm font-black text-slate-950 shadow-xs">
                    {userInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                      {vendorBusinessName || session?.user?.name || "Seller Store"}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      {session?.user?.email || "Vendor Account"}
                    </p>
                    <div className="mt-1">
                      {isApproved ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Active Seller
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                          Under Review
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Drawer Navigation Links */}
              <nav className="space-y-1">
                {visibleLinks.map((link) => {
                  const Icon = link.icon;
                  const active = pathname === link.href;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileDrawerOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition active:scale-98",
                        active
                          ? "bg-amazon-navy text-white shadow-xs dark:bg-[#087DB5] dark:text-white"
                          : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10",
                      )}
                    >
                      <Icon className="h-4.5 w-4.5 shrink-0" />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Drawer Footer Actions */}
            <div className="pt-4 border-t border-slate-200 dark:border-white/10 space-y-2">
              <Link
                href="/"
                onClick={() => setMobileDrawerOpen(false)}
                className="flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10 transition"
              >
                <span className="flex items-center gap-2">
                  <Store className="h-4 w-4 text-amazon-gold" />
                  View Storefront
                </span>
                <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
              </Link>

              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10 transition cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  {mounted && theme === "dark" ? (
                    <Sun className="h-4 w-4 text-amazon-gold" />
                  ) : (
                    <Moon className="h-4 w-4 text-slate-500" />
                  )}
                  {mounted && theme === "dark" ? "Light theme" : "Dark theme"}
                </span>
                <span className="text-[11px] font-bold text-slate-400 uppercase">
                  {theme || "auto"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => signOut({ callbackUrl: "/login" })}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30 transition cursor-pointer"
              >
                <LogOut className="h-4 w-4" />
                Sign out of Seller Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* MAIN CONTAINER: Desktop Sidebar + Responsive Content Area     */}
      {/* ============================================================== */}
      <div className="mx-auto w-full max-w-amazon flex-1 px-3 py-3 sm:px-4 sm:py-6 lg:px-6 lg:py-6 grid gap-6 lg:grid-cols-[250px_1fr]">
        {/* DESKTOP SIDEBAR (Visible only on lg: screens) */}
        <aside className="hidden lg:flex amazon-card h-fit sticky top-6 p-4 flex-col justify-between">
          <div>
            {/* Sidebar Brand Header */}
            <div className="flex items-center justify-between pb-3.5 mb-2 border-b border-slate-200 dark:border-white/10">
              <Link href="/vendor" className="flex items-center gap-2">
                <span className="text-xl font-black tracking-wider text-slate-900 dark:text-white">MOVA</span>
                <span className="rounded bg-amazon-orange px-2 py-0.5 text-[10px] font-black uppercase text-slate-950 shadow-xs">
                  Seller
                </span>
              </Link>
              {!isApproved && !loading && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Review
                </span>
              )}
            </div>

            {/* Sidebar Navigation */}
            <nav className="mt-2 flex flex-col gap-1">
              {visibleLinks.map((link) => {
                const Icon = link.icon;
                const active = pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-xs sm:text-sm font-semibold transition",
                      active
                        ? "bg-amazon-navy text-white shadow-xs dark:bg-white/15 dark:text-white"
                        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Sidebar Footer Controls */}
          <div className="mt-6 pt-3 border-t border-slate-200 dark:border-white/10 space-y-2.5">
            <Link
              href="/"
              className="flex items-center justify-between rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5 transition"
              title="Open customer storefront"
            >
              <span className="flex items-center gap-2">
                <Store className="h-4 w-4 text-amazon-gold" />
                Storefront
              </span>
              <ExternalLink className="h-3 w-3 text-slate-400" />
            </Link>

            <div className="flex items-center justify-between px-2 py-1">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amazon-orange text-[11px] font-black text-slate-950 shadow-xs">
                  {userInitials}
                </div>
                <div className="flex flex-col text-left text-xs min-w-0">
                  <span className="font-bold leading-tight text-slate-900 dark:text-white max-w-[120px] truncate">
                    {vendorBusinessName || session?.user?.name || "Seller"}
                  </span>
                  <span className="text-[10px] text-slate-400 max-w-[120px] truncate">
                    {session?.user?.email || "Vendor Account"}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition cursor-pointer"
                aria-label="Toggle theme"
              >
                {mounted && theme === "dark" ? <Sun className="h-4 w-4 text-amazon-gold" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>

            <button
              type="button"
              onClick={() => signOut({ callbackUrl: "/login" })}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30 transition cursor-pointer"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Content Viewport with mobile bottom navigation safe-padding */}
        <main className="min-w-0 pb-20 lg:pb-6">{children}</main>
      </div>

      {/* ============================================================== */}
      {/* MOBILE BOTTOM NAVIGATION BAR (Visible on mobile/tablet: < lg)  */}
      {/* ============================================================== */}
      <nav
        aria-label="Vendor Mobile Navigation"
        className="fixed bottom-0 inset-x-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border-t border-slate-200/90 dark:border-white/10 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] lg:hidden pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-5 h-14">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const active = item.active;

            return (
              <Link
                key={item.label}
                href={item.href}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold transition-colors select-none active:scale-95",
                  active
                    ? "text-[#087DB5] dark:text-[#00B7B0]"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                )}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-transform",
                      active && "scale-110 text-[#087DB5] dark:text-[#00B7B0]"
                    )}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  {active && (
                    <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#087DB5] dark:bg-[#00B7B0]" />
                  )}
                </div>
                <span className={cn("truncate max-w-[62px]", active && "font-black")}>
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Menu / More Button */}
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className={cn(
              "relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold transition-colors select-none active:scale-95 cursor-pointer",
              mobileDrawerOpen
                ? "text-[#087DB5] dark:text-[#00B7B0]"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            )}
          >
            <div className="relative">
              <MoreHorizontal className="h-5 w-5" strokeWidth={2} />
              {!isApproved && !loading && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
              )}
            </div>
            <span className="truncate max-w-[62px]">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
